import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  Activity,
  AlertCircle,
  Award,
  BarChart3,
  Building,
  Building2,
  Calendar,
  ChevronRight,
  ClipboardList,
  DollarSign,
  Download,
  FileText,
  Filter,
  Flag,
  Home,
  Image as ImageIcon,
  ListTodo,
  MapPin,
  Scale,
  Search,
  Shield,
  TrendingUp,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CartesianGrid,
  Cell,
  LabelList,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Bar,
  BarChart,
} from 'recharts';
import LocationActivityRanking from '../components/LocationActivityRanking';
import MetricCard from '../components/MetricCard';
import ActivityTrends from '../components/pages-specific/ActivityTrends';
import DecisionsPanel from '../components/pages-specific/DecisionsPanel';
import EnforcementPanel from '../components/pages-specific/EnforcementPanel';
import FacilityTypePerformanceTable from '../components/pages-specific/FacilityTypePerformanceTable';
import FollowupCompletion from '../components/pages-specific/FollowupCompletion';
import OverviewCards from '../components/pages-specific/OverviewCards';
import RecommendationsPanel from '../components/pages-specific/RecommendationsPanel';
import TopLocations from '../components/pages-specific/TopLocations';
import PieChart from '../components/PieChart';
import RwandaMap from '../components/RwandaMap';
import SingleLocationSelector from '../components/SingleLocationSelector';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import useLocale from '../contexts/LocaleContext';
import {
  useAssessmentKPIs,
  useComplianceLocationRanking,
  useComplianceQuestionBreakdown,
  useDecisions,
  useFacilitiesRanking,
  useFollowup,
} from '../hooks/useAnalytics';
import { useFlaggedAssessments, useFines, useUpdateAssessmentFlag } from '../hooks/useAssessments';
import { useDashboardOverview } from '../hooks/useDashboard';
import { useFacilityTypes } from '../hooks/useFacilities';
import { analyticsApi, facilitiesApi, questionsApi } from '../lib/api-client';
import { exportToExcel } from '../lib/excel-export';
import { cn } from '../lib/utils';
import type {
  ComplianceLocationRankingItemDto,
  ComplianceQuestionBreakdownDto,
  DecisionItemDto,
  FacilityRankingItemDto,
  FacilityRegistrationAnalyticsDto,
  FacilityRegistrationLocationDto,
  FlaggedAssessmentDto,
  AssessmentFineDto,
} from '../types/api';

type ComplianceTab =
  | 'overview'
  | 'location-ranking'
  | 'activity-ranking'
  | 'question-breakdown'
  | 'facility-ranking'
  | 'flagged'
  | 'fines'
  | 'facility-registration'
  | 'decisions'
  | 'recommendations';

interface BreadcrumbItem {
  id: string;
  name: string;
  type: string;
}

const DECISION_COLORS: Record<string, string> = {
  NO_ACTION: '#94a3b8',
  WARNING: '#f59e0b',
  FINES: '#f97316',
  CLOSURE: '#ef4444',
  RELOCATION_OF_THE_HOUSEHOLD: '#8b5cf6',
  COMPLIANT: '#10b981',
  NON_COMPLIANT: '#ef4444',
};

function fmt(n: number | null | undefined) {
  return (n ?? 0).toFixed(1);
}

function getPercentageColor(p: number): string {
  return p >= 80 ? 'text-green-600' : p >= 60 ? 'text-yellow-600' : p >= 40 ? 'text-orange-600' : 'text-red-600';
}

function getRankBadgeColor(rank: number | null): string {
  if (rank === null) return 'bg-gray-100 text-gray-600';
  if (rank === 1) return 'bg-yellow-100 text-yellow-800';
  if (rank === 2) return 'bg-gray-100 text-gray-800';
  if (rank === 3) return 'bg-orange-100 text-orange-800';
  return 'bg-secondary text-primary';
}

function getScoreColor(score: number): string {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-amber-600';
  if (score >= 40) return 'text-orange-500';
  return 'text-red-500';
}

function getCoverageColor(coverage: number): string {
  if (coverage >= 80) return 'text-green-600';
  if (coverage >= 60) return 'text-amber-600';
  if (coverage >= 40) return 'text-orange-500';
  return 'text-red-500';
}

function optionPct(opt: any, totalResponses: number): number {
  if (opt.percentage != null) return opt.percentage;
  const count = opt.selectionCount ?? opt.count ?? 0;
  return totalResponses > 0 ? (count / totalResponses) * 100 : 0;
}

function optionCount(opt: any): number {
  return opt.selectionCount ?? opt.count ?? 0;
}

function optionColor(pct: number): string {
  return pct >= 80 ? '#10B981' : pct >= 60 ? '#F59E0B' : pct >= 40 ? '#FB923C' : '#EF4444';
}

function getLocalizedFacilityTypeName(facilityTypeId: string, facilityTypes: any[], locale: string): string {
  const ft = facilityTypes.find(f => f.id === facilityTypeId);
  if (!ft) return '';
  switch (locale) {
    case 'fr': return ft.nameFrench || ft.name;
    case 'rw': return ft.nameKinyarwanda || ft.name;
    default: return ft.name;
  }
}

export default function ComplianceDashboard() {
  const { dictionary, locale } = useLocale();
  const [activeTab, setActiveTab] = useState<ComplianceTab>('overview');

  // Shared filters: overview, facility-ranking, flagged, fines, facility-registration
  const [filters, setFilters] = useState<{ month?: string; facilityTypeId?: string; geoLocationId?: string }>({});
  const [locationResetKey, setLocationResetKey] = useState(0);

  const currentMonthLabel = useMemo(() => {
    if (!filters.month) return 'All time';
    return new Date(`${filters.month}-01`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }, [filters.month]);

  // Location ranking: own filter state + drill-down
  const [rankingPeriodMode, setRankingPeriodMode] = useState<'week' | 'dateRange'>('week');
  const [rankingFilters, setRankingFilters] = useState<{
    startDate?: string; endDate?: string; facilityTypeId?: string;
    weekNumber?: number; year?: number; locationType?: string;
  }>({ weekNumber: new Date().getWeek(), year: new Date().getFullYear() });
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([]);
  const [rankingParentId, setRankingParentId] = useState<string | undefined>(undefined);

  // Location ranking export
  const [showRankingExport, setShowRankingExport] = useState(false);
  const [rankingExportLoading, setRankingExportLoading] = useState<string | null>(null);
  const rankingExportRef = useRef<HTMLDivElement>(null);

  // Question breakdown: own filter state
  const [selectedQbFacilityTypeId, setSelectedQbFacilityTypeId] = useState<string>('');
  const [qbDateFilters, setQbDateFilters] = useState<{ startDate?: string; endDate?: string }>({});
  const hasQbFilters = useMemo(() => Object.values(qbDateFilters).some(v => v !== undefined), [qbDateFilters]);
  const [questionBreakdownByQuestionId, setQuestionBreakdownByQuestionId] = useState<Record<string, {
    questionText: string;
    byOption: Record<string, Array<{ facilityName: string; locationName: string; assessmentDate: string; score: number }>>;
  }>>({});
  const [loadingQuestionBreakdownId, setLoadingQuestionBreakdownId] = useState<string | null>(null);
  const [exportingComplianceFacilityResponses, setExportingComplianceFacilityResponses] = useState(false);

  // Facility registration drill-down + filters
  const [facilityRegStack, setFacilityRegStack] = useState<{ id: string; name: string }[]>([]);
  const facilityRegParentId = facilityRegStack.length > 0 ? facilityRegStack[facilityRegStack.length - 1].id : undefined;
  const [facilityRegFacilityTypeId, setFacilityRegFacilityTypeId] = useState('');
  const [facilityRegLocationType, setFacilityRegLocationType] = useState('');
  const [facilityRegStartDate, setFacilityRegStartDate] = useState('');
  const [facilityRegEndDate, setFacilityRegEndDate] = useState('');
  const [facilityRegModalLocation, setFacilityRegModalLocation] = useState<FacilityRegistrationLocationDto | null>(null);
  const [facilityRegModalTypeFilter, setFacilityRegModalTypeFilter] = useState('');
  const [facilityRegModalSearch, setFacilityRegModalSearch] = useState('');
  const [facilityRegModalPage, setFacilityRegModalPage] = useState(1);

  useEffect(() => {
    setFacilityRegModalTypeFilter('');
    setFacilityRegModalSearch('');
    setFacilityRegModalPage(1);
  }, [facilityRegModalLocation?.locationId]);

  // Flagged state
  const [flaggedTypeFilter, setFlaggedTypeFilter] = useState<'HYGIENE' | 'COMPLIANCE' | undefined>('COMPLIANCE');
  const [flaggedLocationId, setFlaggedLocationId] = useState<string | undefined>(undefined);
  const [flaggedPage, setFlaggedPage] = useState(1);

  // KPI toggle
  const [kpiType, setKpiType] = useState<'HYGIENE' | 'COMPLIANCE'>('COMPLIANCE');

  const { data: facilityTypes = [] } = useFacilityTypes();
  // Only show facility types that have at least one compliance questionnaire
  const { data: complianceQuestionnaires = [] } = useQuery({
    queryKey: ['compliance-questionnaires-list'],
    queryFn: () => questionsApi.listComplianceQuestionnaires(),
    staleTime: 30 * 60 * 1000,
  });
  const complianceFacilityTypeIds = useMemo(
    () => new Set(complianceQuestionnaires.map((q: any) => q.facilityTypeId).filter(Boolean)),
    [complianceQuestionnaires],
  );
  const complianceFacilityTypes = useMemo(
    () => complianceFacilityTypeIds.size > 0
      ? facilityTypes.filter(ft => complianceFacilityTypeIds.has(ft.id))
      : facilityTypes,
    [facilityTypes, complianceFacilityTypeIds],
  );

  // Auto-select first compliance facility type for question breakdown
  useEffect(() => {
    if (complianceFacilityTypes.length > 0 && !selectedQbFacilityTypeId) {
      setSelectedQbFacilityTypeId(complianceFacilityTypes[0].id);
    }
  }, [complianceFacilityTypes, selectedQbFacilityTypeId]);

  const selectedFacilityType = complianceFacilityTypes.find(ft => ft.id === selectedQbFacilityTypeId);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (rankingExportRef.current && !rankingExportRef.current.contains(e.target as Node)) {
        setShowRankingExport(false);
      }
    };
    if (showRankingExport) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showRankingExport]);

  // Overview data
  const { data: overviewData, isLoading: overviewLoading } = useDashboardOverview({
    month: filters.month,
    type: 'COMPLIANCE',
    facilityTypeId: filters.facilityTypeId,
    geoLocationId: filters.geoLocationId,
  });
  const { data: kpiData, isLoading: kpiLoading } = useAssessmentKPIs({
    type: kpiType,
    facilityTypeId: filters.facilityTypeId,
  });

  // Derived startDate/endDate from month for compliance-specific API calls
  const derivedStartDate = useMemo(() => {
    if (!filters.month) return undefined;
    return `${filters.month}-01`;
  }, [filters.month]);
  const derivedEndDate = useMemo(() => {
    if (!filters.month) return undefined;
    const [y, m] = filters.month.split('-').map(Number);
    const end = new Date(y, m, 1);
    return end.toISOString().split('T')[0];
  }, [filters.month]);

  const { data: decisions, isLoading: decisionsLoading } = useDecisions({
    startDate: derivedStartDate,
    endDate: derivedEndDate,
    assessmentType: 'COMPLIANCE',
  });
  const { data: followup } = useFollowup({
    startDate: derivedStartDate,
    endDate: derivedEndDate,
    assessmentType: 'COMPLIANCE',
  });

  // Location ranking — strip week/date params depending on mode
  const rankingApiParams = useMemo(() => {
    const { weekNumber, year, startDate, endDate, ...rest } = rankingFilters;
    if (rankingPeriodMode === 'dateRange') return { ...rest, startDate, endDate, parentLocationId: rankingParentId };
    return { ...rest, weekNumber, year, parentLocationId: rankingParentId };
  }, [rankingFilters, rankingPeriodMode, rankingParentId]);

  const { data: locationRanking, isLoading: rankingLoading } = useComplianceLocationRanking(rankingApiParams);

  // Question breakdown
  const { data: questionBreakdown, isLoading: qbLoading } = useComplianceQuestionBreakdown({
    facilityTypeId: selectedQbFacilityTypeId || undefined,
    ...qbDateFilters,
  });

  // Facility ranking
  const { data: topFacilities } = useFacilitiesRanking({ assessmentType: 'COMPLIANCE', limit: 10, direction: 'top', ...filters });
  const { data: bottomFacilities } = useFacilitiesRanking({ assessmentType: 'COMPLIANCE', limit: 10, direction: 'bottom', ...filters });

  // Flagged
  const { data: flaggedData, isLoading: flaggedLoading } = useFlaggedAssessments({
    assessmentType: flaggedTypeFilter,
    locationId: flaggedLocationId,
    page: flaggedPage,
    limit: 20,
  });
  const updateFlagMutation = useUpdateAssessmentFlag();

  // Fines (compliance)
  const [finesPage, setFinesPage] = useState(1);
  const [finesSearch, setFinesSearch] = useState('');
  const [finesSearchInput, setFinesSearchInput] = useState('');
  const { data: finesData, isLoading: finesLoading } = useFines({ assessmentType: 'COMPLIANCE', page: finesPage, limit: 20, search: finesSearch || undefined });
  const finesList = finesData?.data ?? [];
  const finesPagination = finesData?.pagination;

  // Facility registration
  const { data: facilityRegData, isLoading: facilityRegLoading } = useQuery<FacilityRegistrationAnalyticsDto>({
    queryKey: ['facility-registration', facilityRegParentId, facilityRegFacilityTypeId, facilityRegLocationType, facilityRegStartDate, facilityRegEndDate],
    queryFn: () => analyticsApi.getFacilityRegistration({
      parentLocationId: facilityRegParentId,
      facilityTypeId: facilityRegFacilityTypeId || undefined,
      locationType: facilityRegLocationType || undefined,
      startDate: facilityRegStartDate || undefined,
      endDate: facilityRegEndDate || undefined,
    }),
    enabled: activeTab === 'facility-registration',
  });

  const { data: facilityRegFacilities, isLoading: facilityRegFacilitiesLoading } = useQuery({
    queryKey: ['facility-reg-list', facilityRegModalLocation?.locationId, facilityRegModalTypeFilter, facilityRegModalSearch, facilityRegModalPage],
    queryFn: () => facilitiesApi.getAll({
      locationId: facilityRegModalLocation!.locationId,
      facilityTypeId: facilityRegModalTypeFilter || undefined,
      search: facilityRegModalSearch || undefined,
      page: facilityRegModalPage,
      limit: 15,
    }),
    enabled: !!facilityRegModalLocation,
  });

  // Shared filter handlers
  const handleFilterChange = useCallback((key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value || undefined }));
  }, []);
  const clearFilters = useCallback(() => { setFilters({}); setLocationResetKey(k => k + 1); }, []);

  // Ranking filter handlers
  const clearRankingFilters = useCallback(() => {
    setRankingPeriodMode('week');
    setRankingFilters({ weekNumber: new Date().getWeek(), year: new Date().getFullYear() });
    setBreadcrumb([]);
    setRankingParentId(undefined);
  }, []);

  const handleLocationDrillDown = useCallback((item: ComplianceLocationRankingItemDto) => {
    setBreadcrumb(prev => [...prev, { id: item.locationId, name: item.locationName, type: item.locationType }]);
    setRankingParentId(item.locationId);
  }, []);

  const handleBreadcrumbClick = useCallback((index: number) => {
    const next = breadcrumb.slice(0, index + 1);
    setBreadcrumb(next);
    setRankingParentId(next[next.length - 1].id);
  }, [breadcrumb]);

  const handleGoHome = useCallback(() => {
    setBreadcrumb([]);
    setRankingParentId(undefined);
  }, []);

  // Question breakdown handlers
  const clearQbFilters = useCallback(() => setQbDateFilters({}), []);

  useEffect(() => {
    setQuestionBreakdownByQuestionId({});
    setLoadingQuestionBreakdownId(null);
  }, [selectedQbFacilityTypeId, qbDateFilters.startDate, qbDateFilters.endDate]);

  const isYesOption = useCallback((answer: string) => /^(yes|oui|yego)$/i.test(answer.trim()), []);

  const handleGenerateQuestionBreakdown = useCallback(async (questionId: string) => {
    if (!selectedQbFacilityTypeId || !questionBreakdown?.length) return;
    const questionIndex = questionBreakdown.findIndex((q: ComplianceQuestionBreakdownDto) => q.questionId === questionId);
    if (questionIndex < 0) return;
    setLoadingQuestionBreakdownId(questionId);
    setQuestionBreakdownByQuestionId(prev => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
    try {
      const data = await analyticsApi.getComplianceFacilityQuestionnaireResponses(
        selectedQbFacilityTypeId,
        qbDateFilters
      );
      const byOption: Record<string, Array<{ facilityName: string; locationName: string; assessmentDate: string; score: number }>> = {};
      for (const r of data.responses) {
        const answer = (r.answers[questionIndex] ?? '—').trim() || '—';
        if (!byOption[answer]) byOption[answer] = [];
        byOption[answer].push({
          facilityName: r.facilityName,
          locationName: r.locationName,
          assessmentDate: r.assessmentDate,
          score: r.score,
        });
      }
      const question = questionBreakdown[questionIndex];
      setQuestionBreakdownByQuestionId(prev => ({ ...prev, [questionId]: { questionText: question.questionText, byOption } }));
    } catch (e) {
      console.error(e);
      toast.error('Failed to load responses. Please try again.');
    } finally {
      setLoadingQuestionBreakdownId(null);
    }
  }, [selectedQbFacilityTypeId, questionBreakdown, qbDateFilters]);

  const handleExportQuestionBreakdown = useCallback((questionId: string) => {
    const breakdown = questionBreakdownByQuestionId[questionId];
    if (!breakdown || !selectedFacilityType?.name) return;
    const rows: { facility: string; location: string; date: string; score: string; yes: string; no: string }[] = [];
    for (const [response, facilities] of Object.entries(breakdown.byOption)) {
      const tickYes = isYesOption(response) ? '✓' : '';
      const tickNo = !isYesOption(response) ? '✓' : '';
      for (const f of facilities) {
        rows.push({
          facility: f.facilityName,
          location: f.locationName,
          date: new Date(f.assessmentDate).toLocaleDateString(),
          score: `${f.score.toFixed(1)}%`,
          yes: tickYes,
          no: tickNo,
        });
      }
    }
    exportToExcel({
      fileName: `Compliance_Question_Yes_No_${selectedFacilityType.name.replace(/\s+/g, '_')}`,
      sheetName: 'Who responded',
      data: rows,
      columns: [
        { key: 'facility', label: dictionary['dashboard.fines_facility'] ?? 'Facility' },
        { key: 'location', label: dictionary['dashboard.fines_location'] ?? 'Location' },
        { key: 'date', label: dictionary['dashboard.fines_date'] ?? 'Date' },
        { key: 'score', label: dictionary['dashboard.fines_score'] ?? 'Score' },
        { key: 'yes', label: 'Yes' },
        { key: 'no', label: 'No' },
      ],
      title: 'NHIP - Compliance - Who responded Yes/No',
      subtitle: breakdown.questionText.length > 80 ? breakdown.questionText.slice(0, 80) + '…' : breakdown.questionText,
    });
  }, [questionBreakdownByQuestionId, selectedFacilityType, dictionary, isYesOption]);

  const handleExportComplianceSummary = useCallback(() => {
    if (!questionBreakdown?.length || !selectedFacilityType) return;
    exportToExcel({
      fileName: `Compliance_Summary_${selectedFacilityType.name.replace(/\s+/g, '_')}`,
      sheetName: 'Summary',
      data: questionBreakdown.flatMap((q: ComplianceQuestionBreakdownDto, qi: number) =>
        q.options.map(opt => ({
          questionNumber: qi + 1,
          questionText: q.questionText,
          optionText: opt.optionText,
          marks: opt.marks,
          selectionCount: opt.selectionCount,
          totalResponses: q.totalResponses,
          percentage: `${opt.percentage.toFixed(1)}%`,
        }))
      ),
      columns: [
        { key: 'questionNumber', label: 'Q#' },
        { key: 'questionText', label: 'Question' },
        { key: 'optionText', label: 'Option' },
        { key: 'marks', label: 'Marks' },
        { key: 'selectionCount', label: 'Selections' },
        { key: 'totalResponses', label: 'Total Responses' },
        { key: 'percentage', label: 'Percentage' },
      ],
      title: `NHIP - Compliance - ${selectedFacilityType.name}`,
      subtitle: `${questionBreakdown.length} questions`,
    });
  }, [questionBreakdown, selectedFacilityType]);

  const handleExportComplianceFacilityResponses = useCallback(async () => {
    if (!selectedQbFacilityTypeId) return;
    setExportingComplianceFacilityResponses(true);
    try {
      const data = await analyticsApi.getComplianceFacilityQuestionnaireResponses(
        selectedQbFacilityTypeId,
        qbDateFilters
      );
      exportToExcel({
        fileName: `Compliance_Facility_Responses_${(data.facilityTypeName || selectedFacilityType?.name || '').replace(/\s+/g, '_')}`,
        sheetName: 'Facility Responses',
        data: data.responses.map(r => ({
          facility: r.facilityName,
          location: r.locationName,
          date: new Date(r.assessmentDate).toLocaleDateString(),
          score: `${r.score.toFixed(1)}%`,
          assessorName: r.assessorName || '—',
          assessorEmail: r.assessorEmail || '—',
          assessorRole: r.assessorRole || '—',
          ...data.questions.reduce<Record<string, string>>(
            (acc, _q, i) => ({ ...acc, [`q${i + 1}`]: r.answers[i] ?? '—' }),
            {}
          ),
        })),
        columns: [
          { key: 'facility', label: 'Facility' },
          { key: 'location', label: 'Location' },
          { key: 'date', label: 'Assessment Date' },
          { key: 'score', label: 'Score' },
          { key: 'assessorName', label: 'Assessed By' },
          { key: 'assessorEmail', label: 'Assessor Email' },
          { key: 'assessorRole', label: 'Assessor Role' },
          ...data.questions.map((q, i) => ({ key: `q${i + 1}`, label: q })),
        ],
        title: `NHIP - Compliance - ${data.facilityTypeName} - Facility Responses`,
        subtitle: `${data.responses.length} assessments`,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setExportingComplianceFacilityResponses(false);
    }
  }, [selectedQbFacilityTypeId, qbDateFilters, selectedFacilityType]);

  // Location ranking export
  const handleRankingExportExcel = useCallback(() => {
    if (!locationRanking?.rankings?.length) return;
    setRankingExportLoading('excel');
    setShowRankingExport(false);
    try {
      exportToExcel({
        fileName: 'Compliance_Location_Rankings',
        sheetName: 'Rankings',
        data: locationRanking.rankings.map((item: ComplianceLocationRankingItemDto) => ({
          rank: item.rank ?? '-',
          locationName: item.locationName,
          locationType: item.locationType,
          averageScore: `${fmt(item.averageScore)}%`,
          finalScore: `${fmt(item.finalScore)}%`,
          coveragePercentage: `${fmt(item.coveragePercentage)}%`,
          assessedFacilities: `${item.assessedFacilities}/${item.totalFacilities}`,
          above70Count: `${item.above70Count} (${fmt(item.above70Pct)}%)`,
          below70Count: `${item.below70Count} (${fmt(item.below70Pct)}%)`,
          totalAssessments: item.totalAssessments,
          reassessmentCount: item.reassessmentCount,
        })),
        columns: [
          { key: 'rank', label: 'Rank' },
          { key: 'locationName', label: 'Location' },
          { key: 'locationType', label: 'Type' },
          { key: 'averageScore', label: 'Avg Score' },
          { key: 'finalScore', label: 'Final Score' },
          { key: 'coveragePercentage', label: 'Coverage' },
          { key: 'assessedFacilities', label: 'Assessed/Total' },
          { key: 'above70Count', label: 'Above 70%' },
          { key: 'below70Count', label: 'Below 70%' },
          { key: 'totalAssessments', label: 'Inspections' },
          { key: 'reassessmentCount', label: 'Re-inspections' },
        ],
        title: 'NHIP - Compliance Location Rankings',
        subtitle: `${locationRanking.parentLocationName ?? 'National Level'} | ${locationRanking.totalLocations} locations`,
      });
    } catch (e) { console.error(e); } finally { setRankingExportLoading(null); }
  }, [locationRanking]);

  const handleRankingExportPDF = useCallback(() => {
    if (!locationRanking?.rankings?.length) return;
    setRankingExportLoading('pdf');
    setShowRankingExport(false);
    try {
      const doc = new jsPDF();
      doc.setFontSize(16); doc.setFont('helvetica', 'bold');
      doc.text('NHIP - Compliance Location Rankings', 20, 20);
      doc.setFontSize(12); doc.setFont('helvetica', 'normal');
      doc.text(`${locationRanking.parentLocationName ?? 'National Level'} | ${locationRanking.totalLocations} locations`, 20, 30);
      autoTable(doc, {
        startY: 45,
        head: [['Rank', 'Location', 'Type', 'Avg Score', 'Final Score', 'Coverage', 'Assessed/Total', 'Above 70%', 'Below 70%', 'Inspections', 'Re-inspections']],
        body: locationRanking.rankings.map((item: ComplianceLocationRankingItemDto) => [
          item.rank?.toString() ?? '-',
          item.locationName,
          item.locationType,
          `${fmt(item.averageScore)}%`,
          `${fmt(item.finalScore)}%`,
          `${fmt(item.coveragePercentage)}%`,
          `${item.assessedFacilities}/${item.totalFacilities}`,
          `${item.above70Count} (${fmt(item.above70Pct)}%)`,
          `${item.below70Count} (${fmt(item.below70Pct)}%)`,
          item.totalAssessments,
          item.reassessmentCount,
        ]),
        margin: { left: 20, right: 20 },
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [27, 107, 69] },
      });
      doc.save(`Compliance_Location_Rankings_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (e) { console.error(e); } finally { setRankingExportLoading(null); }
  }, [locationRanking]);

  const tabs: { id: ComplianceTab; label: string; icon: React.ReactNode; count?: number }[] = [
    { id: 'overview', label: dictionary['common.overview'] ?? 'Overview', icon: <TrendingUp className='h-4 w-4' /> },
    { id: 'location-ranking', label: dictionary['dashboard.location_ranking'] ?? 'Location Ranking', icon: <Award className='h-4 w-4' /> },
    { id: 'activity-ranking', label: dictionary['dashboard.activity_ranking'] ?? 'Activity', icon: <Activity className='h-4 w-4' /> },
    { id: 'question-breakdown', label: dictionary['compliance.question_breakdown'] ?? 'Questions', icon: <ClipboardList className='h-4 w-4' /> },
    { id: 'facility-ranking', label: dictionary['compliance.facility_ranking'] ?? 'Facilities', icon: <Building className='h-4 w-4' /> },
    { id: 'flagged', label: dictionary['dashboard.flagged'] ?? 'Flagged', icon: <Flag className='h-4 w-4' />, count: flaggedData?.total },
    { id: 'fines', label: dictionary['dashboard.fines'] ?? 'Fines', icon: <Scale className='h-4 w-4' /> },
    { id: 'facility-registration', label: dictionary['dashboard.facility_registration'] ?? 'Facility Registration', icon: <Building2 className='h-4 w-4' /> },
    { id: 'decisions', label: 'Decisions', icon: <Scale className='h-4 w-4' /> },
    { id: 'recommendations', label: 'Recommendations', icon: <ListTodo className='h-4 w-4' /> },
  ];

  // ─── Shared filter bar ───────────────────────────────────────────────────────
  const showSharedFilters = !['location-ranking', 'question-breakdown', 'activity-ranking'].includes(activeTab);

  return (
    <div className='space-y-4'>
      {/* Filter bar — not for ranking / question / activity tabs */}
      {showSharedFilters && (
        <div className='flex flex-wrap items-center gap-2'>
          <Filter className='h-3.5 w-3.5 flex-shrink-0' style={{ color: 'var(--nhip-ink-muted)' }} />
          <Input
            type='month'
            value={filters.month || ''}
            onChange={e => setFilters(prev => ({ ...prev, month: e.target.value || undefined }))}
            className='w-auto text-xs'
            style={{ colorScheme: 'light' }}
          />
          <Input
            type='select'
            value={filters.facilityTypeId || ''}
            onChange={e => setFilters(prev => ({ ...prev, facilityTypeId: (e.target.value as string) || undefined }))}
            placeholder={dictionary['dashboard.all_facility_types'] ?? 'All Facility Types'}
            options={facilityTypes.map(ft => ({ value: ft.id, label: ft.name }))}
            className='w-auto min-w-[150px]'
          />
          <SingleLocationSelector
            key={locationResetKey}
            compact
            selectedLocationId={filters.geoLocationId}
            onLocationChange={id => setFilters(prev => ({ ...prev, geoLocationId: id || undefined }))}
          />
          {filters.month && (
            <span className='inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-xs font-medium'
              style={{ backgroundColor: 'rgba(37,99,235,0.08)', color: '#2563eb' }}>
              {currentMonthLabel}
              <button onClick={() => setFilters(prev => ({ ...prev, month: undefined }))} className='hover:opacity-70'>
                <X className='h-3 w-3' />
              </button>
            </span>
          )}
          {!filters.month && (
            <span className='text-xs font-medium' style={{ color: 'var(--nhip-green)' }}>· All time</span>
          )}
          {(filters.month || filters.facilityTypeId || filters.geoLocationId) && (
            <button onClick={clearFilters} className='ml-1 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors'>
              <X className='h-3 w-3' />Clear
            </button>
          )}
        </div>
      )}

      {/* Sub-tabs */}
      <div className='border-b border-[var(--nhip-border)] -mx-4 sm:-mx-6 px-4 sm:px-6 overflow-x-auto'>
        <nav className='-mb-px flex space-x-6 min-w-max'>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'py-3 px-1 border-b-[3px] font-medium text-sm whitespace-nowrap flex items-center gap-1.5 transition-colors duration-150',
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              {tab.icon}
              {tab.label}
              {tab.count != null && tab.count > 0 && (
                <span className='text-xs font-semibold text-muted-foreground'>{tab.count}</span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* ── OVERVIEW ── */}
      {activeTab === 'overview' && (
        <div className='space-y-3 sm:space-y-4'>
          {/* KPI Cards */}
          <OverviewCards
            data={overviewData!}
            isLoading={overviewLoading}
            kpiData={kpiData}
            kpiLoading={kpiLoading}
            kpiType={kpiType}
            onKpiTypeChange={setKpiType}
          />

          {/* Activity Trends */}
          <ActivityTrends
            limit={3}
            filters={{ month: filters.month, type: 'COMPLIANCE', facilityTypeId: filters.facilityTypeId, geoLocationId: filters.geoLocationId }}
          />

          {/* Map + Facility Type Performance */}
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-3 items-start'>
            {/* Map */}
            <div className='bg-white rounded-lg p-5 shadow-sm flex flex-col gap-3 relative z-0' style={{ border: '1px solid var(--nhip-border)' }}>
              <div className='flex items-center gap-2'>
                <div className='w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border' style={{ backgroundColor: 'var(--nhip-surface)', borderColor: 'var(--nhip-border)' }}>
                  <MapPin className='w-4 h-4' style={{ color: 'var(--nhip-ink-muted)' }} />
                </div>
                <div>
                  <p className='text-sm font-semibold' style={{ color: 'var(--nhip-ink)' }}>{dictionary['dashboard.facilities_map']}</p>
                  <p className='text-xs' style={{ color: 'var(--nhip-ink-muted)' }}>{dictionary['dashboard.map_description']}</p>
                </div>
              </div>
              <RwandaMap height={340} params={{ ...filters, assessmentType: 'COMPLIANCE' }} />
              <div className='flex flex-wrap items-center gap-x-3 gap-y-1.5 pt-2' style={{ borderTop: '1px solid var(--nhip-border)' }}>
                {[
                  { hex: '#22c55e', label: `${dictionary['dashboard.excellent']} 90–100%` },
                  { hex: '#eab308', label: `${dictionary['dashboard.good']} 75–89%` },
                  { hex: '#f97316', label: `${dictionary['dashboard.fair']} 60–74%` },
                  { hex: '#ef4444', label: `${dictionary['dashboard.poor']} <60%` },
                ].map(l => (
                  <div key={l.hex} className='flex items-center gap-1.5 text-xs' style={{ color: 'var(--nhip-ink-muted)' }}>
                    <div className='w-2.5 h-2.5 rounded-full flex-shrink-0' style={{ backgroundColor: l.hex }} />
                    {l.label}
                  </div>
                ))}
              </div>
            </div>

            {/* Facility Type Performance */}
            <div className='bg-white rounded-lg p-5 shadow-sm flex flex-col gap-3' style={{ border: '1px solid var(--nhip-border)' }}>
              <div className='flex items-center gap-2'>
                <div className='w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border' style={{ backgroundColor: 'var(--nhip-surface)', borderColor: 'var(--nhip-border)' }}>
                      <BarChart3 className='w-4 h-4' style={{ color: 'var(--nhip-ink-muted)' }} />
                </div>
                <div>
                  <p className='text-sm font-semibold' style={{ color: 'var(--nhip-ink)' }}>{dictionary['dashboard.facility_type_performance']}</p>
                  <p className='text-xs' style={{ color: 'var(--nhip-ink-muted)' }}>Avg compliance score · sorted highest to lowest</p>
                </div>
              </div>
              <FacilityTypePerformanceBarChart filters={{ month: filters.month, type: 'COMPLIANCE', geoLocationId: filters.geoLocationId }} locale={locale} facilityTypes={facilityTypes} />
            </div>
          </div>

          {/* Decisions + Followup 2×2 */}
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
            <div className='bg-white rounded-lg border border-gray-200 p-4'>
              <h3 className='text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2'>
                <AlertCircle className='h-4 w-4 text-orange-600' />
                {dictionary['dashboard.decisions_breakdown'] ?? 'Decisions'}
              </h3>
              {decisionsLoading ? (
                <div className='flex justify-center py-10'><div className='h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-primary' /></div>
              ) : decisions && decisions.decisions?.length ? (
                <div className='space-y-2'>
                  <p className='text-xs text-gray-500 mb-3'>Total: <span className='font-semibold text-gray-700'>{decisions.totalAssessments}</span></p>
                  {decisions.decisions.map((item: DecisionItemDto) => (
                    <div key={item.decision} className='flex items-center gap-2'>
                      <span className='text-xs text-gray-600 w-36 truncate flex-shrink-0'>{item.decision.replace(/_/g, ' ')}</span>
                      <div className='flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden'>
                        <div className='h-full rounded-full' style={{ width: `${item.percentage ?? 0}%`, backgroundColor: DECISION_COLORS[item.decision] ?? '#94a3b8' }} />
                      </div>
                      <span className='text-xs font-medium text-gray-700 w-16 text-right flex-shrink-0'>
                        {item.count} ({(item.percentage ?? 0).toFixed(1)}%)
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className='text-sm text-gray-400 text-center py-10'>{dictionary['dashboard.no_data_message']}</p>
              )}
            </div>

            <div className='bg-white rounded-lg border border-gray-200 p-4'>
              <h3 className='text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2'>
                <Calendar className='h-4 w-4 text-primary' />
                {dictionary['dashboard.followup_tracking'] ?? 'Followup Tracking'}
              </h3>
              {followup ? (
                <div className='grid grid-cols-2 gap-3'>
                  {[
                    { label: dictionary['dashboard.with_followup'] ?? 'With Followup', value: followup.totalWithFollowup ?? '—', color: 'blue' },
                    { label: dictionary['dashboard.followup_completed'] ?? 'Completed', value: followup.completedFollowups ?? '—', color: 'green' },
                    { label: dictionary['dashboard.followup_pending'] ?? 'Pending', value: followup.pendingFollowups ?? '—', color: 'yellow' },
                    { label: dictionary['dashboard.followup_overdue'] ?? 'Overdue', value: followup.overdueFollowups ?? '—', color: 'red' },
                  ].map(c => (
                    <div key={c.label} className='text-center p-3 bg-gray-50 rounded-lg'>
                      <div className={`text-xl font-bold text-${c.color}-700`}>{c.value}</div>
                      <div className='text-xs text-gray-500 mt-0.5'>{c.label}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className='text-sm text-gray-400 text-center py-8'>{dictionary['dashboard.no_data_message']}</p>
              )}
            </div>
          </div>

          {/* Enforcement panel */}
          <div className='w-full lg:w-1/2'>
            <EnforcementPanel filters={{ month: filters.month, type: 'COMPLIANCE', facilityTypeId: filters.facilityTypeId, geoLocationId: filters.geoLocationId }} />
          </div>

          {/* Facility type table + followup completion + top locations */}
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-3 items-start'>
            <FacilityTypePerformanceTable filters={{ month: filters.month, type: 'COMPLIANCE', geoLocationId: filters.geoLocationId }} />
            <div className='flex flex-col gap-3'>
              <FollowupCompletion filters={{ month: filters.month, type: 'COMPLIANCE', facilityTypeId: filters.facilityTypeId, geoLocationId: filters.geoLocationId }} />
              <TopLocations filters={{ month: filters.month, type: 'COMPLIANCE', facilityTypeId: filters.facilityTypeId, geoLocationId: filters.geoLocationId }} />
            </div>
          </div>
        </div>
      )}

      {/* ── LOCATION RANKING ── */}
      {activeTab === 'location-ranking' && (
        <div className='space-y-4 sm:space-y-6'>
          <Card className='w-full'>
            <CardHeader className='pb-2 sm:pb-3 pt-3 sm:pt-6'>
              <CardTitle className='flex items-center gap-2 text-base sm:text-lg'>
                <Award className='h-4 w-4 sm:h-5 sm:w-5' />
                {dictionary['dashboard.location_ranking'] ?? 'Location Ranking'}
              </CardTitle>
              <CardDescription className='text-xs sm:text-sm'>
                {dictionary['compliance.location_ranking_description'] ?? 'Compliance performance by location'}
              </CardDescription>
            </CardHeader>
            <CardContent className='pb-3 sm:pb-6'>
              <div className='flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 sm:items-end'>
                <div className='grid grid-cols-2 sm:flex gap-2 sm:gap-2 flex-wrap'>
                  {/* Period mode toggle */}
                  <div className='col-span-2 sm:col-span-1 flex gap-2 items-end'>
                    <Input
                      type='select'
                      value={rankingPeriodMode}
                      onChange={e => setRankingPeriodMode(e.target.value as 'week' | 'dateRange')}
                      options={[
                        { value: 'week', label: dictionary['assessments.by_week'] ?? 'By week' },
                        { value: 'dateRange', label: dictionary['assessments.by_date_range'] ?? 'Date range' },
                      ]}
                      className='w-auto min-w-[140px] text-sm'
                    />
                  </div>
                  {rankingPeriodMode === 'week' && (
                    <>
                      <div>
                        <label className='block text-xs font-medium text-gray-700 mb-1'>{dictionary['location_ranking.week'] ?? 'Week'}</label>
                        <Input type='number' min='1' max='53' value={rankingFilters.weekNumber ?? ''} onChange={e => setRankingFilters(prev => ({ ...prev, weekNumber: parseInt(e.target.value) || undefined }))} className='w-full sm:w-20 text-sm' />
                      </div>
                      <div>
                        <label className='block text-xs font-medium text-gray-700 mb-1'>{dictionary['location_ranking.year'] ?? 'Year'}</label>
                        <Input type='number' min='2020' max='2030' value={rankingFilters.year ?? ''} onChange={e => setRankingFilters(prev => ({ ...prev, year: parseInt(e.target.value) || undefined }))} className='w-full sm:w-24 text-sm' />
                      </div>
                    </>
                  )}
                  {rankingPeriodMode === 'dateRange' && (
                    <>
                      <div>
                        <label className='block text-xs font-medium text-gray-700 mb-1'>{dictionary['dashboard.start_date'] ?? 'Start'}</label>
                        <Input type='date' value={rankingFilters.startDate || ''} onChange={e => setRankingFilters(prev => ({ ...prev, startDate: e.target.value || undefined }))} className='w-full sm:w-36 text-sm' />
                      </div>
                      <div>
                        <label className='block text-xs font-medium text-gray-700 mb-1'>{dictionary['dashboard.end_date'] ?? 'End'}</label>
                        <Input type='date' value={rankingFilters.endDate || ''} onChange={e => setRankingFilters(prev => ({ ...prev, endDate: e.target.value || undefined }))} className='w-full sm:w-36 text-sm' />
                      </div>
                    </>
                  )}
                  {/* Location type */}
                  <div className='col-span-2 sm:col-span-1'>
                    <label className='block text-xs font-medium text-gray-700 mb-1'>{dictionary['location_ranking.location_type'] ?? 'Location Type'}</label>
                    <Input
                      type='select'
                      value={rankingFilters.locationType || ''}
                      onChange={e => setRankingFilters(prev => ({ ...prev, locationType: (e.target.value as string) || undefined }))}
                      placeholder={dictionary['location_ranking.all_types'] ?? 'All Types'}
                      options={[
                        { value: 'PROVINCE', label: dictionary['location.option_province'] ?? 'Province' },
                        { value: 'DISTRICT', label: dictionary['location.option_district'] ?? 'District' },
                        { value: 'SECTOR', label: dictionary['location.option_sector'] ?? 'Sector' },
                        { value: 'CELL', label: dictionary['location.option_cell'] ?? 'Cell' },
                        { value: 'VILLAGE', label: dictionary['location.option_village'] ?? 'Village' },
                      ]}
                      className='w-full sm:w-32 text-sm'
                    />
                  </div>
                  {/* Facility type */}
                  <div className='col-span-2 sm:col-span-1'>
                    <label className='block text-xs font-medium text-gray-700 mb-1'>{dictionary['location_ranking.facility_type'] ?? 'Facility Type'}</label>
                    <Input
                      type='select'
                      value={rankingFilters.facilityTypeId || ''}
                      onChange={e => setRankingFilters(prev => ({ ...prev, facilityTypeId: (e.target.value as string) || undefined }))}
                      placeholder={dictionary['location_ranking.all_facility_types'] ?? 'All Facility Types'}
                      options={facilityTypes.map(ft => ({ value: ft.id, label: ft.name }))}
                      className='w-full sm:w-40 text-sm'
                    />
                  </div>
                </div>
                <Button onClick={clearRankingFilters} variant='outline' size='sm' className='w-full sm:w-auto text-sm'>
                  {dictionary['location_ranking.clear_filters'] ?? 'Clear Filters'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {breadcrumb.length > 0 && (
            <Card className='w-full'>
              <CardContent className='py-2 sm:py-3'>
                <div className='flex items-center gap-1 sm:gap-2 text-xs sm:text-sm overflow-x-auto'>
                  <button onClick={handleGoHome} className='flex items-center gap-1 text-primary hover:text-primary font-medium whitespace-nowrap flex-shrink-0'>
                    <Home className='h-3 w-3 sm:h-4 sm:w-4' />
                    <span>{dictionary['location_ranking.all_locations'] ?? 'All Locations'}</span>
                  </button>
                  {breadcrumb.map((crumb, i) => (
                    <div key={crumb.id} className='flex items-center gap-1 sm:gap-2 flex-shrink-0'>
                      <span className='text-gray-400'>/</span>
                      <button onClick={() => handleBreadcrumbClick(i)} className='text-primary hover:text-primary font-medium whitespace-nowrap'>
                        {crumb.name}
                      </button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card className='w-full'>
            <CardHeader className='pb-2 sm:pb-3 pt-3 sm:pt-6'>
              <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
                <div className='flex-1 min-w-0'>
                  <CardTitle className='text-base sm:text-lg'>
                    {locationRanking?.parentLocationName ? `${locationRanking.parentLocationName} ${dictionary['location_ranking.rankings'] ?? 'Rankings'}` : dictionary['location_ranking.rankings'] ?? 'Rankings'}
                  </CardTitle>
                  <CardDescription className='text-xs sm:text-sm'>
                    {locationRanking?.startDate && locationRanking?.endDate
                      ? `${locationRanking.startDate} – ${locationRanking.endDate}`
                      : locationRanking?.weekNumber
                        ? `${dictionary['location_ranking.week_of'] ?? 'Week'} ${locationRanking.weekNumber} ${dictionary['location_ranking.of'] ?? 'of'} ${locationRanking.year}`
                        : null}
                    {' '}• {locationRanking?.totalLocations ?? 0} {dictionary['location_ranking.locations'] ?? 'locations'}
                    {locationRanking?.childLocationType && ` • ${locationRanking.childLocationType.toLowerCase()}s`}
                  </CardDescription>
                </div>
                {locationRanking?.rankings?.length > 0 && (
                  <div className='relative flex-shrink-0' ref={rankingExportRef}>
                    <Button onClick={() => setShowRankingExport(!showRankingExport)} variant='outline' size='sm'
                      className='flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-primary hover:text-primary/80 border-none shadow-none hover:bg-secondary py-1 px-2 sm:px-3'
                      disabled={rankingExportLoading !== null}>
                      <Download className='h-3 w-3 sm:h-4 sm:w-4' />
                      <span className='hidden sm:inline'>{rankingExportLoading ? 'Exporting...' : 'Export'}</span>
                    </Button>
                    {showRankingExport && (
                      <div className='absolute right-0 top-full mt-1 w-40 sm:w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50'>
                        <div className='p-2'>
                          <button onClick={handleRankingExportExcel} disabled={rankingExportLoading !== null} className='w-full flex items-center gap-2 px-3 py-2 text-sm text-green-700 hover:bg-green-50 rounded-md disabled:opacity-50'>
                            <FileText className='h-4 w-4' />Excel
                          </button>
                          <button onClick={handleRankingExportPDF} disabled={rankingExportLoading !== null} className='w-full flex items-center gap-2 px-3 py-2 text-sm text-red-700 hover:bg-red-50 rounded-md disabled:opacity-50'>
                            <FileText className='h-4 w-4' />PDF
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {rankingLoading ? (
                <div className='flex items-center justify-center h-64'>
                  <div className='text-center'>
                    <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2' />
                    <p className='text-gray-600'>{dictionary['location_ranking.loading'] ?? 'Loading...'}</p>
                  </div>
                </div>
              ) : !locationRanking?.rankings?.length ? (
                <div className='flex items-center justify-center h-64'>
                  <div className='text-center'>
                    <Award className='h-12 w-12 text-gray-400 mx-auto mb-4' />
                    <p className='text-gray-600'>{dictionary['dashboard.no_data_message'] ?? 'No data'}</p>
                    <p className='text-sm text-gray-500'>{dictionary['location_ranking.try_filters'] ?? 'Try adjusting the filters'}</p>
                  </div>
                </div>
              ) : (
                <div className='overflow-x-auto -mx-4 sm:mx-0'>
                  <div className='inline-block min-w-full align-middle'>
                    <table className='min-w-full'>
                      <thead>
                        <tr className='border-b border-gray-200'>
                          <th className='text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs sm:text-sm'>{dictionary['common.rank'] ?? 'Rank'}</th>
                          <th className='text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs sm:text-sm'>{dictionary['common.location'] ?? 'Location'}</th>
                          <th className='text-right py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs sm:text-sm'>{dictionary['dashboard.avg_score'] ?? 'Avg Score'}</th>
                          <th className='text-right py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs sm:text-sm hidden sm:table-cell'>Final Score</th>
                          <th className='text-right py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs sm:text-sm hidden md:table-cell'>{dictionary['dashboard.coverage'] ?? 'Coverage'}</th>
                          <th className='text-right py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs sm:text-sm hidden md:table-cell'>Assessed / Total</th>
                          <th className='text-right py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs sm:text-sm hidden lg:table-cell'>Above 70%</th>
                          <th className='text-right py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs sm:text-sm hidden lg:table-cell'>Below 70%</th>
                          <th className='text-right py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs sm:text-sm hidden lg:table-cell'>Inspections</th>
                          <th className='text-right py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs sm:text-sm hidden lg:table-cell'>Re-inspections</th>
                          <th className='text-center py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs sm:text-sm'>{dictionary['common.actions'] ?? 'Actions'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {locationRanking.rankings.map((item: ComplianceLocationRankingItemDto) => (
                          <tr key={item.locationId} className='border-b border-gray-100 hover:bg-gray-50'>
                            <td className='py-2 sm:py-3 px-2 sm:px-4'>
                              <span className={`inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full text-xs sm:text-sm font-medium ${getRankBadgeColor(item.rank)}`}>
                                {item.rank ?? '-'}
                              </span>
                            </td>
                            <td className='py-2 sm:py-3 px-2 sm:px-4'>
                              <div className='font-medium text-gray-900 text-xs sm:text-sm'>{item.locationName}</div>
                              <div className='text-xs text-gray-500'>{item.locationType}</div>
                            </td>
                            <td className='py-2 sm:py-3 px-2 sm:px-4 text-right'>
                              <span className={`font-medium text-xs sm:text-sm ${getScoreColor(item.averageScore)}`}>{fmt(item.averageScore)}%</span>
                            </td>
                            <td className='py-2 sm:py-3 px-2 sm:px-4 text-right hidden sm:table-cell'>
                              <span className={`font-semibold text-xs sm:text-sm ${getScoreColor(item.finalScore)}`}>{fmt(item.finalScore)}%</span>
                            </td>
                            <td className='py-2 sm:py-3 px-2 sm:px-4 text-right hidden md:table-cell'>
                              <div className='flex items-center justify-end gap-2'>
                                <span className={`font-medium text-xs sm:text-sm ${getCoverageColor(item.coveragePercentage)}`}>{fmt(item.coveragePercentage)}%</span>
                                <div className='w-12 sm:w-16 bg-gray-200 rounded-full h-2'>
                                  <div
                                    className={`h-2 rounded-full ${item.coveragePercentage >= 80 ? 'bg-green-500' : item.coveragePercentage >= 60 ? 'bg-yellow-500' : item.coveragePercentage >= 40 ? 'bg-orange-500' : 'bg-red-500'}`}
                                    style={{ width: `${Math.min(item.coveragePercentage, 100)}%` }}
                                  />
                                </div>
                              </div>
                            </td>
                            <td className='py-2 sm:py-3 px-2 sm:px-4 text-right hidden md:table-cell'>
                              <div className='text-xs text-gray-600'>
                                <span className='font-medium'>{item.assessedFacilities}</span>
                                <span className='text-gray-400'> / {item.totalFacilities}</span>
                              </div>
                            </td>
                            <td className='py-2 sm:py-3 px-2 sm:px-4 text-right hidden lg:table-cell'>
                              <div className='flex items-center justify-end gap-1.5'>
                                <span className='text-xs font-semibold text-green-600'>{item.above70Count.toLocaleString()}</span>
                                <span className='text-[10px] text-gray-400'>{fmt(item.above70Pct)}%</span>
                              </div>
                            </td>
                            <td className='py-2 sm:py-3 px-2 sm:px-4 text-right hidden lg:table-cell'>
                              <div className='flex items-center justify-end gap-1.5'>
                                <span className='text-xs font-semibold text-red-500'>{item.below70Count.toLocaleString()}</span>
                                <span className='text-[10px] text-gray-400'>{fmt(item.below70Pct)}%</span>
                              </div>
                            </td>
                            <td className='py-2 sm:py-3 px-2 sm:px-4 text-right hidden lg:table-cell'>
                              <span className='text-gray-600 text-xs'>{item.totalAssessments.toLocaleString()}</span>
                            </td>
                            <td className='py-2 sm:py-3 px-2 sm:px-4 text-right hidden lg:table-cell'>
                              <span className='text-gray-600 text-xs'>{item.reassessmentCount.toLocaleString()}</span>
                            </td>
                            <td className='py-2 sm:py-3 px-2 sm:px-4 text-center'>
                              {item.locationType !== 'VILLAGE' ? (
                                <button onClick={() => handleLocationDrillDown(item)}
                                  className='inline-flex items-center gap-1 text-primary hover:text-primary text-xs sm:text-sm font-medium'>
                                  <ChevronRight className='h-3 w-3 sm:h-4 sm:w-4' />
                                  <span className='hidden sm:inline'>{dictionary['dashboard.expand'] ?? 'Expand'}</span>
                                </button>
                              ) : (
                                <span className='text-gray-400 text-xs sm:text-sm'>-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── ACTIVITY RANKING ── */}
      {activeTab === 'activity-ranking' && (
        <div className='-mx-4 sm:-mx-6'>
          <LocationActivityRanking type='COMPLIANCE' />
        </div>
      )}

      {/* ── QUESTION BREAKDOWN ── */}
      {activeTab === 'question-breakdown' && (
        <div className='bg-white rounded-lg border border-gray-200 p-4 sm:p-6'>
          {complianceFacilityTypes.length > 0 && (
            <div className='mb-4 border-b border-gray-200'>
              <nav className='flex gap-1 overflow-x-auto py-1'>
                {complianceFacilityTypes.map(ft => (
                  <button key={ft.id} type='button' onClick={() => setSelectedQbFacilityTypeId(ft.id)} title={ft.name}
                    className={`flex-shrink-0 px-3 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors whitespace-nowrap max-w-[200px] sm:max-w-[280px] truncate ${
                      selectedQbFacilityTypeId === ft.id ? 'border-primary text-primary bg-secondary' : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}>
                    {ft.name}
                  </button>
                ))}
              </nav>
            </div>
          )}

          {selectedQbFacilityTypeId && (
            <div className='mb-4 p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200'>
              <div className='flex items-center justify-between mb-3'>
                <h3 className='text-xs sm:text-sm font-medium text-gray-900 flex items-center gap-1'>
                  <Filter className='h-3 w-3' />{dictionary['dashboard.filter_analytics'] ?? 'Filter'}
                </h3>
                {hasQbFilters && (
                  <Button variant='outline' size='sm' onClick={clearQbFilters} className='flex items-center gap-1 text-xs h-7'>
                    <X className='h-3 w-3' /><span className='hidden sm:inline'>{dictionary['dashboard.clear_filters']}</span><span className='sm:hidden'>Clear</span>
                  </Button>
                )}
              </div>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                <div>
                  <label className='block text-xs font-medium text-gray-700 mb-1'>
                    <Calendar className='inline h-3 w-3 mr-1' />{dictionary['dashboard.start_date'] ?? 'Start Date'}
                  </label>
                  <Input type='date' value={qbDateFilters.startDate || ''} onChange={e => setQbDateFilters(prev => ({ ...prev, startDate: e.target.value || undefined }))} className='text-sm h-8' />
                </div>
                <div>
                  <label className='block text-xs font-medium text-gray-700 mb-1'>
                    <Calendar className='inline h-3 w-3 mr-1' />{dictionary['dashboard.end_date'] ?? 'End Date'}
                  </label>
                  <Input type='date' value={qbDateFilters.endDate || ''} onChange={e => setQbDateFilters(prev => ({ ...prev, endDate: e.target.value || undefined }))} className='text-sm h-8' />
                </div>
              </div>
            </div>
          )}

          {selectedFacilityType && (
            <div className='bg-secondary rounded-lg p-3 border border-primary/30 mb-4'>
              <div className='flex items-start justify-between gap-3'>
                <div>
                  <h3 className='text-sm font-medium text-primary mb-1'>{selectedFacilityType.name}</h3>
                  <div className='flex flex-col sm:flex-row gap-2 sm:gap-4 text-xs text-primary'>
                    <span>{dictionary['dashboard.facility_type_label'] ?? 'Facility Type'}: {selectedFacilityType.name}</span>
                    {questionBreakdown && <span>{dictionary['common.questions'] ?? 'Questions'}: {questionBreakdown.length}</span>}
                  </div>
                </div>
                {questionBreakdown && questionBreakdown.length > 0 && (
                  <div className='flex flex-col sm:flex-row gap-2 flex-shrink-0'>
                    <button
                      onClick={handleExportComplianceSummary}
                      className='flex items-center gap-2 px-3 py-1.5 text-xs sm:text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors justify-center'
                    >
                      <Download className='h-3.5 w-3.5' /><span>Summary</span>
                    </button>
                    <button
                      onClick={handleExportComplianceFacilityResponses}
                      disabled={exportingComplianceFacilityResponses}
                      className='flex items-center gap-2 px-3 py-1.5 text-xs sm:text-sm text-green-600 hover:bg-green-50 rounded-lg transition-colors justify-center disabled:opacity-50'
                    >
                      <Download className='h-3.5 w-3.5' />
                      <span>{exportingComplianceFacilityResponses ? 'Exporting…' : 'Facility Responses'}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {qbLoading ? (
            <div className='flex items-center justify-center py-12'>
              <div className='h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-primary' />
            </div>
          ) : questionBreakdown && questionBreakdown.length > 0 ? (
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
              {questionBreakdown.map((question: ComplianceQuestionBreakdownDto, index: number) => (
                <div key={question.questionId} className='border rounded-lg p-3 sm:p-4'>
                  <div className='mb-3'>
                    <h4 className='text-sm font-medium text-gray-900 mb-1'>
                      {dictionary['dashboard.question'] ?? 'Q'}{index + 1}: {question.questionText}
                    </h4>
                    <div className='text-xs text-gray-500'>
                      {dictionary['dashboard.responses'] ?? 'Responses'}: {question.totalResponses}
                    </div>
                  </div>
                  <div className='grid grid-cols-2 gap-2 mb-3'>
                    {question.options.map(opt => {
                      const pct = optionPct(opt, question.totalResponses);
                      const cnt = optionCount(opt);
                      return (
                        <div key={opt.optionText} className='text-center p-2 bg-gray-50 rounded-lg'>
                          <div className={`text-base sm:text-lg font-bold ${getPercentageColor(pct)}`}>{pct.toFixed(1)}%</div>
                          <div className='text-xs text-gray-600 mt-0.5 break-words'>{opt.optionText}</div>
                          <div className='text-xs text-gray-500 mt-0.5'>{cnt} / {question.totalResponses}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div className='flex justify-center my-3'>
                    <PieChart
                      title={dictionary['dashboard.response_distribution'] ?? 'Response Distribution'}
                      labels={question.options.map(o => o.optionText)}
                      series={question.options.map(o => optionCount(o))}
                      colors={question.options.map(o => optionColor(optionPct(o, question.totalResponses)))}
                      width={240}
                    />
                  </div>
                  <div className='mt-3 space-y-1.5'>
                    <h5 className='text-xs font-medium text-gray-700 mb-2'>{dictionary['dashboard.detailed_breakdown'] ?? 'Detailed Breakdown'}</h5>
                    {question.options.map(opt => {
                      const pct = optionPct(opt, question.totalResponses);
                      const cnt = optionCount(opt);
                      return (
                        <div key={opt.optionText} className='flex items-center justify-between p-2 bg-gray-50 rounded text-xs'>
                          <div className='flex items-center gap-2 flex-1 min-w-0'>
                            <div className='w-3 h-3 rounded flex-shrink-0' style={{ backgroundColor: optionColor(pct) }} />
                            <span className='font-medium text-gray-700 truncate'>{opt.optionText}</span>
                          </div>
                          <div className='flex items-center gap-2 sm:gap-3 flex-shrink-0 ml-2'>
                            <span className={`font-bold ${getPercentageColor(pct)}`}>{pct.toFixed(1)}%</span>
                            <span className='text-gray-600'>{cnt}/{question.totalResponses}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className='mt-3 pt-3 border-t border-gray-200'>
                    {loadingQuestionBreakdownId === question.questionId ? (
                      <div className='flex items-center justify-center py-2 text-xs text-gray-500'>
                        <span className='inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-primary mr-2' />
                        {dictionary['dashboard.generating'] ?? 'Generating…'}
                      </div>
                    ) : questionBreakdownByQuestionId[question.questionId] ? (
                      <>
                        <div className='flex items-center justify-between mb-2'>
                          <h5 className='text-xs font-medium text-gray-700'>{dictionary['dashboard.who_responded'] ?? 'Who responded'}</h5>
                          <button onClick={() => handleExportQuestionBreakdown(question.questionId)}
                            className='flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary hover:bg-secondary rounded'>
                            <Download className='h-3 w-3' />{dictionary['dashboard.download_breakdown'] ?? 'Download'}
                          </button>
                        </div>
                        <div className='grid grid-cols-2 gap-2'>
                          {Object.entries(questionBreakdownByQuestionId[question.questionId].byOption).map(([optionText, facilities]) => (
                            <div key={optionText} className='rounded border border-gray-100 bg-gray-50 p-2'>
                              <div className='text-xs font-semibold text-gray-800 mb-1'>{optionText} ({facilities.length})</div>
                              <ul className='text-xs text-gray-600 space-y-0.5 max-h-28 overflow-y-auto'>
                                {facilities.map((f, idx) => (
                                  <li key={idx} className='truncate' title={`${f.facilityName} – ${f.locationName}`}>
                                    {f.facilityName}{f.locationName ? ` · ${f.locationName}` : ''}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <Button type='button' variant='outline' size='sm' onClick={() => handleGenerateQuestionBreakdown(question.questionId)} className='w-full text-xs'>
                        {dictionary['dashboard.generate_breakdown'] ?? 'Generate who responded Yes/No'}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className='text-center py-8 text-gray-500'>
              <ClipboardList className='h-8 w-8 mx-auto mb-2 text-gray-400' />
              <p className='text-sm'>{dictionary['dashboard.no_data_message']}</p>
            </div>
          )}
        </div>
      )}

      {/* ── FACILITY RANKING ── */}
      {activeTab === 'facility-ranking' && (
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
          <div className='bg-white rounded-lg border border-gray-200 p-4'>
            <h3 className='text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2'>
              <Award className='h-4 w-4 text-green-600' />
              {dictionary['dashboard.top_facilities'] ?? 'Top Facilities'}
            </h3>
            <div className='space-y-2'>
              {topFacilities?.facilities?.map((f: FacilityRankingItemDto) => (
                <div key={f.facilityId} className='flex items-center gap-2'>
                  <span className='w-6 h-6 rounded-full bg-green-100 text-green-700 text-xs font-bold flex items-center justify-center flex-shrink-0'>{f.rank}</span>
                  <div className='flex-1 min-w-0'>
                    <a href={`/facilities/${f.facilityId}`} className='text-sm font-medium text-gray-900 hover:text-primary truncate block'>{f.facilityName}</a>
                    <span className='text-xs text-gray-400 truncate block'>{f.locationName}</span>
                  </div>
                  <span className='text-sm font-bold text-green-600 flex-shrink-0'>{f.latestScore != null ? f.latestScore.toFixed(1) : '—'}%</span>
                </div>
              )) ?? <p className='text-sm text-gray-400 text-center py-6'>{dictionary['dashboard.no_data_message']}</p>}
            </div>
          </div>
          <div className='bg-white rounded-lg border border-gray-200 p-4'>
            <h3 className='text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2'>
              <AlertCircle className='h-4 w-4 text-red-600' />
              {dictionary['dashboard.bottom_facilities'] ?? 'Needs Attention'}
            </h3>
            <div className='space-y-2'>
              {bottomFacilities?.facilities?.map((f: FacilityRankingItemDto) => (
                <div key={f.facilityId} className='flex items-center gap-2'>
                  <span className='w-6 h-6 rounded-full bg-red-100 text-red-700 text-xs font-bold flex items-center justify-center flex-shrink-0'>{f.rank}</span>
                  <div className='flex-1 min-w-0'>
                    <a href={`/facilities/${f.facilityId}`} className='text-sm font-medium text-gray-900 hover:text-primary truncate block'>{f.facilityName}</a>
                    <span className='text-xs text-gray-400 truncate block'>{f.locationName}</span>
                  </div>
                  <span className='text-sm font-bold text-red-600 flex-shrink-0'>{f.latestScore != null ? f.latestScore.toFixed(1) : '—'}%</span>
                </div>
              )) ?? <p className='text-sm text-gray-400 text-center py-6'>{dictionary['dashboard.no_data_message']}</p>}
            </div>
          </div>
        </div>
      )}

      {/* ── FLAGGED ── */}
      {activeTab === 'flagged' && (
        <div className='bg-white rounded-lg border border-gray-200 p-4 sm:p-6'>
          <h3 className='text-base sm:text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2'>
            <Flag className='h-4 w-4 text-amber-600' />
            {dictionary['dashboard.flagged'] ?? 'Flagged Assessments'}
          </h3>
          <div className='mb-4'>
            <SingleLocationSelector
              selectedLocationId={flaggedLocationId}
              onLocationChange={(id) => { setFlaggedLocationId(id || undefined); setFlaggedPage(1); }}
            />
          </div>
          {flaggedData?.byType != null && (
            <div className='flex flex-wrap gap-2 mb-4'>
              <button
                onClick={() => { setFlaggedTypeFilter(undefined); setFlaggedPage(1); }}
                className={`px-3 py-1.5 rounded-md text-sm font-medium ${flaggedTypeFilter == null ? 'bg-secondary text-primary' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                {dictionary['common.all'] ?? 'All'} ({(flaggedData.byType.HYGIENE ?? 0) + (flaggedData.byType.COMPLIANCE ?? 0)})
              </button>
              <button
                onClick={() => { setFlaggedTypeFilter('HYGIENE'); setFlaggedPage(1); }}
                className={`px-3 py-1.5 rounded-md text-sm font-medium ${flaggedTypeFilter === 'HYGIENE' ? 'bg-secondary text-primary' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                {dictionary['assessment_detail.type_hygiene'] ?? 'Hygiene'} ({flaggedData.byType.HYGIENE ?? 0})
              </button>
              <button
                onClick={() => { setFlaggedTypeFilter('COMPLIANCE'); setFlaggedPage(1); }}
                className={`px-3 py-1.5 rounded-md text-sm font-medium ${flaggedTypeFilter === 'COMPLIANCE' ? 'bg-secondary text-primary' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                {dictionary['assessment_detail.type_compliance'] ?? 'Compliance'} ({flaggedData.byType.COMPLIANCE ?? 0})
              </button>
            </div>
          )}
          {flaggedLoading ? (
            <div className='flex justify-center py-12'><div className='h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-primary' /></div>
          ) : !flaggedData?.data?.length ? (
            <p className='text-sm text-gray-500 text-center py-8'>{dictionary['dashboard.no_data_message'] ?? 'No flagged assessments.'}</p>
          ) : (
            <div className='overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='border-b border-gray-200 text-left text-gray-600'>
                    <th className='pb-2 pr-4 font-medium'>{dictionary['dashboard.fines_facility'] ?? 'Facility'}</th>
                    <th className='pb-2 pr-4 font-medium hidden sm:table-cell'>{dictionary['dashboard.fines_location'] ?? 'Location'}</th>
                    <th className='pb-2 pr-4 font-medium'>{dictionary['dashboard.facility_type_label'] ?? 'Type'}</th>
                    <th className='pb-2 pr-4 font-medium'>{dictionary['dashboard.fines_score'] ?? 'Score'}</th>
                    <th className='pb-2 pr-4 font-medium'>{dictionary['dashboard.fines_date'] ?? 'Date'}</th>
                    <th className='pb-2 pl-2 font-medium'>{dictionary['common.actions'] ?? 'Actions'}</th>
                  </tr>
                </thead>
                <tbody>
                  {flaggedData.data.map((item: FlaggedAssessmentDto) => (
                    <tr key={item.id} className='border-b border-gray-100 hover:bg-gray-50'>
                      <td className='py-3 pr-4 font-medium text-gray-900'>
                        <a href={`/assessments/${item.id}`} className='text-primary hover:underline'>{item.facilityName}</a>
                      </td>
                      <td className='py-3 pr-4 text-gray-600 hidden sm:table-cell max-w-[240px]'>{item.locationName}</td>
                      <td className='py-3 pr-4 text-gray-600'>{item.facilityTypeName}</td>
                      <td className='py-3 pr-4'>
                        <span className={item.percentageScore >= 50 ? 'text-green-600' : 'text-red-600'}>{item.percentageScore.toFixed(1)}%</span>
                      </td>
                      <td className='py-3 pr-4 text-gray-600'>{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '—'}</td>
                      <td className='py-3 pl-2'>
                        <button
                          onClick={() => updateFlagMutation.mutate({ id: item.id, isFlagged: false })}
                          disabled={updateFlagMutation.isPending}
                          className='text-amber-600 hover:text-amber-800 text-xs font-medium'
                        >
                          {dictionary['dashboard.unflag'] ?? 'Unflag'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {flaggedData && flaggedData.total > 20 && (
            <div className='mt-4 flex items-center justify-between text-sm text-gray-600'>
              <span>
                {dictionary['common.showing'] ?? 'Showing'} {(flaggedPage - 1) * 20 + 1}–{Math.min(flaggedPage * 20, flaggedData.total)} {dictionary['common.of'] ?? 'of'} {flaggedData.total}
              </span>
              <div className='flex gap-2'>
                <button onClick={() => setFlaggedPage(p => Math.max(1, p - 1))} disabled={flaggedPage <= 1}
                  className='px-2 py-1 rounded border border-gray-300 disabled:opacity-50'>
                  {dictionary['common.previous'] ?? 'Previous'}
                </button>
                <button onClick={() => setFlaggedPage(p => p + 1)} disabled={flaggedPage * 20 >= flaggedData.total}
                  className='px-2 py-1 rounded border border-gray-300 disabled:opacity-50'>
                  {dictionary['common.next'] ?? 'Next'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── FINES ── */}
      {activeTab === 'fines' && (
        <div className='bg-white rounded-lg border border-gray-200 p-4 sm:p-6 space-y-4'>
          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
            <h3 className='text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2'>
              <DollarSign className='h-4 w-4 text-amber-600' />
              {dictionary['dashboard.fines'] ?? 'Compliance Assessments with Fines'}
            </h3>
            <form onSubmit={e => { e.preventDefault(); setFinesSearch(finesSearchInput); setFinesPage(1); }} className='flex gap-1'>
              <input
                type='text'
                value={finesSearchInput}
                onChange={e => setFinesSearchInput(e.target.value)}
                placeholder='Search facility, NID, phone…'
                className='h-8 px-3 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary w-48 sm:w-56'
              />
              <button type='submit'
                className='h-8 px-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-500 hover:text-gray-700'>
                <Search className='h-3.5 w-3.5' />
              </button>
              {finesSearch && (
                <button type='button' onClick={() => { setFinesSearch(''); setFinesSearchInput(''); setFinesPage(1); }}
                  className='h-8 px-2 text-xs text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg'>
                  ✕
                </button>
              )}
            </form>
          </div>

          {/* Stat cards */}
          {finesData?.stats && (
            <div className='grid grid-cols-3 gap-3'>
              <div className='rounded-xl border border-gray-200 bg-gray-50 p-3 sm:p-4'>
                <p className='text-xs text-gray-500 mb-1'>Total Fines</p>
                <p className='text-base sm:text-xl font-bold text-gray-900'>
                  {finesData.stats.totalAmount.toLocaleString()} <span className='text-xs font-normal text-gray-400'>RWF</span>
                </p>
                <p className='text-xs text-gray-400 mt-0.5'>{finesData.stats.totalCount.toLocaleString()} assessments</p>
              </div>
              <div className='rounded-xl border border-red-100 bg-red-50 p-3 sm:p-4'>
                <p className='text-xs text-red-500 mb-1'>Outstanding</p>
                <p className='text-base sm:text-xl font-bold text-red-600'>
                  {finesData.stats.outstandingAmount.toLocaleString()} <span className='text-xs font-normal text-red-400'>RWF</span>
                </p>
                <p className='text-xs text-red-400 mt-0.5'>
                  {finesData.stats.totalAmount > 0
                    ? `${((finesData.stats.outstandingAmount / finesData.stats.totalAmount) * 100).toFixed(0)}% of total`
                    : '0% of total'}
                </p>
              </div>
              <div className='rounded-xl border border-green-100 bg-green-50 p-3 sm:p-4'>
                <p className='text-xs text-green-600 mb-1'>Collected</p>
                <p className='text-base sm:text-xl font-bold text-green-700'>
                  {finesData.stats.collectedAmount.toLocaleString()} <span className='text-xs font-normal text-green-400'>RWF</span>
                </p>
                <p className='text-xs text-green-500 mt-0.5'>
                  {finesData.stats.totalAmount > 0
                    ? `${((finesData.stats.collectedAmount / finesData.stats.totalAmount) * 100).toFixed(0)}% of total`
                    : '0% of total'}
                </p>
              </div>
            </div>
          )}

          {finesLoading ? (
            <div className='flex justify-center py-12'><div className='h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-primary' /></div>
          ) : finesList.length === 0 ? (
            <p className='text-sm text-gray-500 text-center py-8'>{dictionary['dashboard.no_fines'] ?? 'No compliance assessments with fines.'}</p>
          ) : (<>
            <div className='overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='border-b border-gray-200 text-left text-gray-600'>
                    <th className='pb-2 pr-4 font-medium'>{dictionary['dashboard.fines_facility'] ?? 'Facility'}</th>
                    <th className='pb-2 pr-4 font-medium hidden sm:table-cell'>{dictionary['dashboard.fines_location'] ?? 'Location'}</th>
                    <th className='pb-2 pr-4 font-medium'>{dictionary['dashboard.fines_amount'] ?? 'Fine amount'}</th>
                    <th className='pb-2 pr-4 font-medium'>{dictionary['dashboard.fines_score'] ?? 'Score'}</th>
                    <th className='pb-2 pr-4 font-medium'>{dictionary['dashboard.fines_date'] ?? 'Date'}</th>
                    <th className='pb-2 pl-2 font-medium'>{dictionary['dashboard.fines_pictures'] ?? 'Pictures'}</th>
                  </tr>
                </thead>
                <tbody>
                  {finesList.map((item: AssessmentFineDto) => (
                    <tr key={item.id} className='border-b border-gray-100 hover:bg-gray-50'>
                      <td className='py-3 pr-4 font-medium text-gray-900'>
                        <a href={`/assessments/${item.id}`} className='text-primary hover:underline'>{item.facilityTitle}</a>
                      </td>
                      <td className='py-3 pr-4 text-gray-600 hidden sm:table-cell max-w-[180px] truncate'>{item.location}</td>
                      <td className='py-3 pr-4'>
                        <span className='font-medium text-amber-700'>{item.fineAmount?.toLocaleString?.() ?? item.fineAmount}</span>
                      </td>
                      <td className='py-3 pr-4'>
                        <span className={item.score >= 50 ? 'text-green-600' : 'text-red-600'}>{item.score.toFixed(1)}%</span>
                      </td>
                      <td className='py-3 pr-4 text-gray-600'>{item.date ? new Date(item.date).toLocaleDateString() : '—'}</td>
                      <td className='py-3 pl-2'>
                        {item.pictures?.length ? (
                          <div className='flex gap-1 flex-wrap'>
                            {item.pictures.slice(0, 3).map((url: string, i: number) => (
                              <a key={i} href={url} target='_blank' rel='noopener noreferrer'
                                className='inline-flex items-center justify-center w-8 h-8 rounded border border-gray-200 bg-gray-50 hover:border-primary/30'>
                                <ImageIcon className='h-4 w-4 text-gray-500' />
                              </a>
                            ))}
                            {item.pictures.length > 3 && <span className='text-xs text-gray-500'>+{item.pictures.length - 3}</span>}
                          </div>
                        ) : <span className='text-gray-400'>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {finesPagination && finesPagination.totalPages > 1 && (
              <div className='flex items-center justify-between pt-2 border-t border-gray-100 mt-2'>
                <span className='text-xs text-gray-500'>Page {finesPagination.currentPage} of {finesPagination.totalPages} · {finesPagination.totalItems} total</span>
                <div className='flex gap-2'>
                  <button onClick={() => setFinesPage(p => p - 1)} disabled={!finesPagination.hasPrevious}
                    className='px-3 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed'>Previous</button>
                  <button onClick={() => setFinesPage(p => p + 1)} disabled={!finesPagination.hasNext}
                    className='px-3 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed'>Next</button>
                </div>
              </div>
            )}
          </>)}
        </div>
      )}

      {/* ── FACILITY REGISTRATION ── */}
      {activeTab === 'facility-registration' && (
        <div className='bg-white rounded-lg border border-gray-200 p-4 sm:p-6 space-y-4'>
          <div className='flex items-start justify-between'>
            <div>
              <h3 className='text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2'>
                <Building2 className='h-4 w-4 text-primary' />
                {dictionary['dashboard.facility_registration'] ?? 'Facility Registration'}
              </h3>
              <p className='text-xs text-gray-600 mt-0.5'>
                {dictionary['dashboard.facility_registration_description'] ?? 'Distribution of registered facilities by location.'}
              </p>
            </div>
            {(facilityRegStack.length > 0 || facilityRegFacilityTypeId || facilityRegLocationType || facilityRegStartDate || facilityRegEndDate) && (
              <button
                onClick={() => { setFacilityRegStack([]); setFacilityRegFacilityTypeId(''); setFacilityRegLocationType(''); setFacilityRegStartDate(''); setFacilityRegEndDate(''); setFacilityRegModalLocation(null); }}
                className='text-xs text-primary hover:underline flex items-center gap-1'
              >
                <X className='h-3 w-3' /> {dictionary['dashboard.facility_reg_reset'] ?? 'Reset'}
              </button>
            )}
          </div>

          <div className='grid grid-cols-2 sm:grid-cols-4 gap-3'>
            <Input type='select' label={dictionary['dashboard.facility_type'] ?? 'Facility Type'} value={facilityRegFacilityTypeId}
              onChange={e => { setFacilityRegFacilityTypeId(String(e.target.value)); setFacilityRegModalLocation(null); }}
              placeholder={dictionary['dashboard.facility_reg_all_types'] ?? 'All types'}
              options={[
                { value: '', label: dictionary['dashboard.facility_reg_all_types'] ?? 'All types' },
                ...facilityTypes.map(ft => ({ value: ft.id, label: ft.name })),
              ]}
            />
            <Input type='select' label={dictionary['dashboard.group_by_level'] ?? 'Group by Level'} value={facilityRegLocationType}
              onChange={e => { setFacilityRegLocationType(String(e.target.value)); setFacilityRegStack([]); setFacilityRegModalLocation(null); }}
              placeholder={dictionary['dashboard.default'] ?? 'Default'}
              options={[
                { value: '', label: dictionary['dashboard.default'] ?? 'Default' },
                { value: 'PROVINCE', label: dictionary['dashboard.province'] ?? 'Province' },
                { value: 'DISTRICT', label: dictionary['dashboard.district'] ?? 'District' },
                { value: 'SECTOR', label: dictionary['dashboard.sector'] ?? 'Sector' },
                { value: 'CELL', label: dictionary['dashboard.cell'] ?? 'Cell' },
                { value: 'VILLAGE', label: dictionary['dashboard.village'] ?? 'Village' },
              ]}
            />
            <Input type='date' label={dictionary['dashboard.start_date'] ?? 'Start Date'} value={facilityRegStartDate} onChange={e => setFacilityRegStartDate(e.target.value)} />
            <Input type='date' label={dictionary['dashboard.end_date'] ?? 'End Date'} value={facilityRegEndDate} onChange={e => setFacilityRegEndDate(e.target.value)} />
          </div>

          {facilityRegStack.length > 0 && (
            <div className='flex items-center gap-1 text-xs text-gray-500 flex-wrap'>
              <button className='hover:text-primary flex items-center gap-0.5' onClick={() => { setFacilityRegStack([]); setFacilityRegModalLocation(null); }}>
                <MapPin className='h-3 w-3' />{dictionary['dashboard.all'] ?? 'All'}
              </button>
              {facilityRegStack.map((item, i) => (
                <span key={item.id} className='flex items-center gap-1'>
                  <ChevronRight className='h-3 w-3' />
                  <button className='hover:text-primary' onClick={() => { setFacilityRegStack(prev => prev.slice(0, i + 1)); setFacilityRegModalLocation(null); }}>
                    {item.name}
                  </button>
                </span>
              ))}
            </div>
          )}

          {facilityRegLoading ? (
            <div className='flex justify-center py-12'><div className='h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-primary' /></div>
          ) : !facilityRegData?.locations.length ? (
            <p className='text-sm text-gray-500 text-center py-8'>{dictionary['dashboard.no_data_for_filters'] ?? 'No data available for the selected filters.'}</p>
          ) : (
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
              <PieChart
                labels={facilityRegData.locations.map(l => l.locationName)}
                series={facilityRegData.locations.map(l => l.totalFacilities)}
                colors={facilityRegData.locations.map((_, i) => {
                  const p = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1'];
                  return p[i % p.length];
                })}
                width={360}
              />
              <div className='space-y-1.5 max-h-[420px] overflow-y-auto pr-1'>
                <p className='text-xs text-gray-500 mb-2 sticky top-0 bg-white pb-1'>
                  {facilityRegData.parentLocationName ? `${facilityRegData.parentLocationName} — ` : ''}
                  <span className='font-medium text-gray-700'>{facilityRegData.totalFacilities}</span> {dictionary['dashboard.total_facilities_count'] ?? 'total facilities'}
                </p>
                {facilityRegData.locations.map((loc, i) => {
                  const palette = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1'];
                  const color = palette[i % palette.length];
                  return (
                    <button key={loc.locationId} type='button' onClick={() => setFacilityRegModalLocation(loc)}
                      className='w-full text-left p-2.5 rounded-lg border border-gray-100 hover:border-primary/30 hover:bg-secondary/50 transition-colors flex items-center justify-between gap-3'>
                      <div className='flex items-center gap-2 min-w-0'>
                        <span className='w-2.5 h-2.5 rounded-full flex-shrink-0' style={{ backgroundColor: color }} />
                        <span className='text-xs font-medium text-gray-900 truncate'>{loc.locationName}</span>
                      </div>
                      <div className='flex items-center gap-1.5 flex-shrink-0'>
                        <span className='text-sm font-bold text-gray-800'>{loc.totalFacilities}</span>
                        <ChevronRight className='h-3.5 w-3.5 text-gray-400' />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {facilityRegModalLocation && (
            <div className='fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center' onClick={() => setFacilityRegModalLocation(null)}>
              <div className='bg-white rounded-xl shadow-xl flex flex-col' style={{ maxHeight: '90vh', width: '80vw' }} onClick={e => e.stopPropagation()}>
                <div className='flex items-start justify-between px-5 pt-5 pb-3 border-b border-gray-100 flex-shrink-0'>
                  <div>
                    <h3 className='text-base font-semibold text-gray-900'>{facilityRegModalLocation.locationName}</h3>
                    <p className='text-xs text-gray-500 mt-0.5'>
                      <span className='text-xl font-bold text-gray-800 mr-1'>{facilityRegModalLocation.totalFacilities}</span>
                      {dictionary['dashboard.facilities_registered'] ?? 'facilities registered'}
                    </p>
                  </div>
                  <button onClick={() => setFacilityRegModalLocation(null)} className='text-gray-400 hover:text-gray-600 mt-0.5'><X className='h-4 w-4' /></button>
                </div>
                {facilityRegModalLocation.byFacilityType && facilityRegModalLocation.byFacilityType.length > 0 && (
                  <div className='px-5 py-3 border-b border-gray-100 flex-shrink-0'>
                    <div className='flex flex-wrap gap-1.5'>
                      <button type='button' onClick={() => { setFacilityRegModalTypeFilter(''); setFacilityRegModalPage(1); }}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${!facilityRegModalTypeFilter ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                        {dictionary['dashboard.all'] ?? 'All'} ({facilityRegModalLocation.totalFacilities})
                      </button>
                      {facilityRegModalLocation.byFacilityType.map((ft: any) => (
                        <button key={ft.facilityTypeId} type='button' onClick={() => { setFacilityRegModalTypeFilter(ft.facilityTypeId); setFacilityRegModalPage(1); }}
                          className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${facilityRegModalTypeFilter === ft.facilityTypeId ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                          {ft.facilityTypeName} ({ft.count})
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className='px-5 py-2.5 border-b border-gray-100 flex-shrink-0'>
                  <Input type='text' placeholder={dictionary['dashboard.search_placeholder'] ?? 'Search by name or ID…'} value={facilityRegModalSearch}
                    onChange={e => { setFacilityRegModalSearch(e.target.value); setFacilityRegModalPage(1); }}
                    inputPrefix={<Search className='h-3.5 w-3.5' />} />
                </div>
                <div className='overflow-y-auto flex-1'>
                  {facilityRegFacilitiesLoading ? (
                    <div className='flex justify-center py-10'><div className='h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-primary' /></div>
                  ) : !facilityRegFacilities?.data.length ? (
                    <p className='text-sm text-gray-400 text-center py-10'>{dictionary['dashboard.no_facilities_found'] ?? 'No facilities found.'}</p>
                  ) : (
                    <table className='w-full text-sm'>
                      <thead className='sticky top-0 bg-white border-b border-gray-100'>
                        <tr className='text-left text-[11px] text-gray-400 font-medium'>
                          <th className='px-5 py-1.5'>{dictionary['dashboard.fines_facility'] ?? 'Facility'}</th>
                          <th className='px-5 py-1.5 hidden sm:table-cell'>{dictionary['dashboard.type'] ?? 'Type'}</th>
                          <th className='px-5 py-1.5 hidden sm:table-cell'>{dictionary['dashboard.contact'] ?? 'Contact'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {facilityRegFacilities.data.map((fac: any) => (
                          <tr key={fac.id} className='border-b border-gray-50 hover:bg-gray-50'>
                            <td className='px-5 py-1.5'>
                              <div className='text-xs font-medium text-gray-900 leading-tight'>{fac.title}</div>
                              <div className='text-[10px] text-gray-400'>{fac.identifierNumber}</div>
                            </td>
                            <td className='px-5 py-1.5 hidden sm:table-cell'>
                              <span className='text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded'>
                                {locale === 'rw' ? fac.facilityType?.nameKinyarwanda : locale === 'fr' ? fac.facilityType?.nameFrench : fac.facilityType?.name}
                              </span>
                            </td>
                            <td className='px-5 py-1.5 text-xs text-gray-500 hidden sm:table-cell'>{fac.contactNumber ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                <div className='flex items-center justify-between px-5 py-3 border-t border-gray-100 flex-shrink-0 gap-3'>
                  <div className='flex items-center gap-1.5'>
                    {(facilityRegFacilities?.total ?? 0) > 15 && (
                      <>
                        <button onClick={() => setFacilityRegModalPage(p => Math.max(1, p - 1))} disabled={facilityRegModalPage <= 1}
                          className='px-2.5 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50'>
                          {dictionary['dashboard.prev'] ?? 'Prev'}
                        </button>
                        <span className='text-xs text-gray-500'>
                          {facilityRegModalPage} / {Math.ceil((facilityRegFacilities?.total ?? 0) / 15)}
                        </span>
                        <button onClick={() => setFacilityRegModalPage(p => p + 1)} disabled={facilityRegModalPage * 15 >= (facilityRegFacilities?.total ?? 0)}
                          className='px-2.5 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50'>
                          {dictionary['dashboard.next'] ?? 'Next'}
                        </button>
                      </>
                    )}
                  </div>
                  <button type='button'
                    onClick={() => { setFacilityRegStack(prev => [...prev, { id: facilityRegModalLocation.locationId, name: facilityRegModalLocation.locationName }]); setFacilityRegModalLocation(null); }}
                    className='flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary border border-primary/30 rounded-lg hover:bg-secondary transition-colors'>
                    <ChevronRight className='h-4 w-4' /> {dictionary['dashboard.drill_down'] ?? 'Drill Down'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Decisions */}
      {activeTab === 'decisions' && <DecisionsPanel assessmentType='COMPLIANCE' />}

      {/* Recommendations */}
      {activeTab === 'recommendations' && <RecommendationsPanel assessmentType='COMPLIANCE' />}
    </div>
  );
}

// ─── Inline facility-type bar chart (compliance) ──────────────────────────────
function FacilityTypePerformanceBarChart({ filters, locale, facilityTypes }: {
  filters: { month?: string; type?: string; geoLocationId?: string };
  locale: string;
  facilityTypes: any[];
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-facility-type-performance', filters],
    queryFn: async () => {
      const { dashboardApi } = await import('../lib/api-client');
      return dashboardApi.getFacilityTypePerformance(filters as any);
    },
    staleTime: 10 * 60 * 1000,
  });

  const sorted = useMemo(() => {
    const items: any[] = (data as any)?.items ?? [];
    return [...items]
      .filter(t => (t.totalAssessments ?? 0) > 0)
      .sort((a, b) => b.averageScore - a.averageScore)
      .map(t => ({
        name: getLocalizedFacilityTypeName(t.facilityTypeId, facilityTypes, locale) || t.facilityTypeId,
        score: parseFloat((t.averageScore ?? 0).toFixed(1)),
        facilities: t.totalFacilities ?? 0,
        assessments: t.totalAssessments ?? 0,
      }));
  }, [data, facilityTypes, locale]);

  if (isLoading) {
    return (
      <div className='space-y-2'>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className='flex items-center gap-3 animate-pulse'>
            <div className='h-3 rounded w-32' style={{ backgroundColor: 'var(--nhip-border)' }} />
            <div className='flex-1 h-4 rounded' style={{ backgroundColor: 'var(--nhip-border)', opacity: 0.6 - i * 0.1 }} />
            <div className='h-3 rounded w-10' style={{ backgroundColor: 'var(--nhip-border)' }} />
          </div>
        ))}
      </div>
    );
  }

  if (!sorted.length) {
    return <div className='text-center py-12 text-sm' style={{ color: 'var(--nhip-ink-muted)' }}>No data</div>;
  }

  const barH = 24;
  const chartH = Math.max(200, sorted.length * (barH + 10));
  const yAxisW = Math.min(180, Math.max(100, sorted.reduce((m: number, d: any) => Math.max(m, d.name.length * 6), 0)));

  return (
    <>
      <div style={{ height: Math.min(440, chartH), overflowY: sorted.length > 16 ? 'auto' : 'visible' }}>
        <div style={{ height: chartH, minWidth: 0 }}>
          <ResponsiveContainer width='100%' height='100%'>
            <BarChart layout='vertical' data={sorted} margin={{ top: 0, right: 48, bottom: 0, left: 4 }} barCategoryGap='22%'>
              <CartesianGrid strokeDasharray='3 3' horizontal={false} stroke='var(--nhip-border)' />
              <XAxis type='number' domain={[0, 100]} tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={v => `${v}%`} axisLine={false} tickLine={false} tickCount={6} />
              <YAxis type='category' dataKey='name' width={yAxisW} tick={{ fontSize: 10, fill: '#374151' }} axisLine={false} tickLine={false} />
              <Tooltip
                cursor={{ fill: 'rgba(0,0,0,0.03)' }}
                contentStyle={{ border: '1px solid var(--nhip-border)', borderRadius: 6, fontSize: 12, padding: '8px 12px' }}
                formatter={(_value: unknown, _name: string, props: any) => [
                  `${props.payload?.score.toFixed(1)}% avg · ${props.payload?.facilities} facilities · ${props.payload?.assessments} assessments`,
                  'Score',
                ]}
              />
              <Bar dataKey='score' radius={[0, 4, 4, 0]} maxBarSize={barH}>
                {sorted.map((entry: any, i: number) => {
                  const c = entry.score >= 90 ? '#22c55e' : entry.score >= 75 ? '#eab308' : entry.score >= 60 ? '#f97316' : '#ef4444';
                  return <Cell key={i} fill={c} fillOpacity={0.8} />;
                })}
                <LabelList dataKey='score' position='right' formatter={(v: number) => `${v.toFixed(1)}%`} style={{ fontSize: 10, fill: '#6b7280', fontWeight: 600 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className='flex flex-wrap items-center gap-x-3 gap-y-1.5 pt-2' style={{ borderTop: '1px solid var(--nhip-border)' }}>
        {[
          { hex: '#22c55e', label: 'Excellent ≥90%' },
          { hex: '#eab308', label: 'Good 75–89%' },
          { hex: '#f97316', label: 'Fair 60–74%' },
          { hex: '#ef4444', label: 'Poor <60%' },
        ].map(l => (
          <div key={l.label} className='flex items-center gap-1.5 text-xs' style={{ color: 'var(--nhip-ink-muted)' }}>
            <div className='w-2.5 h-2.5 rounded-sm flex-shrink-0' style={{ backgroundColor: l.hex }} />
            {l.label}
          </div>
        ))}
      </div>
    </>
  );
}

// ISO week helper (mirrors LocationRanking.tsx)
declare global {
  interface Date {
    getWeek(): number;
  }
}
if (!Date.prototype.getWeek) {
  Date.prototype.getWeek = function () {
    const date = new Date(this.getTime());
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
    const week1 = new Date(date.getFullYear(), 0, 4);
    return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
  };
}
